/**
 * trackingEnvironment.ts — central host guard for all Google tracking.
 *
 * `shouldEnableProductionTracking()` returns true ONLY when the current
 * hostname is an owned production brand host from src/lib/brandRegistry.ts.
 * Preview (*.lovable.app / id-preview--*), localhost, dev/test builds and
 * unknown hosts always return false.
 *
 * When tracking is disabled the reason is recorded on
 * `window.__OBC_TRACKING_DISABLED_REASON__` for diagnostics.
 */

import { BRAND_REGISTRY, isPreviewHost } from './brandRegistry';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '']);

// Build the allowlist from the brand registry: every host every real brand
// serves is an approved production host. We include both apex and www where
// the registry lists them (it does for microsites; for alias domains we still
// respect the registry because those are owned brand-controlled origins).
const PRODUCTION_HOST_ALLOWLIST: ReadonlySet<string> = new Set(
  BRAND_REGISTRY.flatMap((b) => b.hostnames).map((h) => h.toLowerCase()),
);

export function getProductionHostAllowlist(): string[] {
  return Array.from(PRODUCTION_HOST_ALLOWLIST).sort();
}

function setDisabledReason(reason: string): void {
  if (typeof window === 'undefined') return;
  try {
    (window as any).__OBC_TRACKING_DISABLED_REASON__ = reason;
  } catch {
    /* ignore */
  }
}

function clearDisabledReason(): void {
  if (typeof window === 'undefined') return;
  try {
    delete (window as any).__OBC_TRACKING_DISABLED_REASON__;
  } catch {
    /* ignore */
  }
}

export function isProductionHost(hostname: string): boolean {
  return PRODUCTION_HOST_ALLOWLIST.has(hostname.toLowerCase());
}

export function shouldEnableProductionTracking(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = (window.location.hostname || '').toLowerCase();

  // Dev/test build modes
  try {
    const mode = (import.meta as any)?.env?.MODE as string | undefined;
    const isProd = (import.meta as any)?.env?.PROD as boolean | undefined;
    if (mode === 'development' || mode === 'test' || isProd === false) {
      setDisabledReason(`build_mode:${mode ?? 'unknown'}`);
      return false;
    }
  } catch {
    /* import.meta unavailable — continue */
  }

  if (LOCAL_HOSTS.has(hostname)) {
    setDisabledReason(`localhost:${hostname}`);
    return false;
  }
  if (isPreviewHost(hostname)) {
    setDisabledReason(`preview_host:${hostname}`);
    return false;
  }
  if (!PRODUCTION_HOST_ALLOWLIST.has(hostname)) {
    setDisabledReason(`unapproved_host:${hostname}`);
    return false;
  }

  clearDisabledReason();
  return true;
}

/**
 * Cheap synchronous check used by index.html-style guards. Does NOT read
 * import.meta — safe to call very early.
 */
export function isApprovedBrandHost(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  if (LOCAL_HOSTS.has(h)) return false;
  if (isPreviewHost(h)) return false;
  return PRODUCTION_HOST_ALLOWLIST.has(h);
}
